import React, { MouseEvent, ReactNode, Ref, useMemo } from 'react';
import {
  FormControl,
  FormControlProps,
  FormHelperText,
  FormLabel,
  ToggleButton,
  ToggleButtonGroup,
  ToggleButtonGroupProps,
} from '@mui/material';
import IncFormHelperText, {
  IncFormHelperTextProps,
} from 'components/forms/inc-form-helper-text/inc-form-helper-text.component';
import { v4 as uuidv4 } from 'uuid';

export type ToggleButtonGroupBaseOption = {
  /**
   * Rendered as the button's content, so it takes a node as well as a word — an
   * alignment option pairs its icon with its label.
   */
  label: ReactNode;
  value: string;
  /**
   * Set it per option and you own the whole answer for that option, including whatever
   * the group was disabled for. These are passed straight to `ToggleButton`, and MUI
   * takes a prop passed in over the one the group's context supplies -- the context's
   * value is used only where the passed one is `undefined`. So a computed `false` here
   * does not fall back to the group's `true`, it overrules it: write
   * `disabled: disabled || <your reason>`, never `disabled: <your reason>`.
   */
  disabled?: boolean;
};

export type ToggleButtonGroupBaseType = {
  label?: string;
  options: ToggleButtonGroupBaseOption[];
  name?: string;
  onChange: (value: any) => void;
  FormControlProps?: FormControlProps;
  'data-testid'?: string;
  toggleButtonSize?: 'large' | 'medium' | 'small' | 'extraSmall';
} & ToggleButtonGroupProps &
  Omit<IncFormHelperTextProps, 'onChange'>;

const ToggleButtonGroupBase = React.forwardRef(
  (
    {
      label,
      options,
      exclusive,
      helperText,
      severity,
      error,
      onChange,
      value,
      name,
      'data-testid': testId = 'ToggleButtonGroupField',
      FormControlProps = {},
      toggleButtonSize = 'small',
      ...rest
    }: ToggleButtonGroupBaseType,
    ref,
  ) => {
    const handleChange = (
      _event: MouseEvent<HTMLElement>,
      newValue: string | string[],
    ) => {
      if (!!exclusive && !newValue) {
        return;
      }

      onChange(newValue);
    };

    const finalName = useMemo(() => name ?? uuidv4(), [name]);

    return (
      <FormControl
        data-testid={testId}
        sx={{ alignItems: 'flex-start' }}
        margin="normal"
        error={!!error}
        {...FormControlProps}
      >
        {label && <FormLabel id={`${finalName}-label`}>{label}</FormLabel>}
        <ToggleButtonGroup
          {...rest}
          exclusive={exclusive}
          color="primary"
          onChange={handleChange}
          value={value}
          /*
           * Only when a label is actually rendered. A group given no label — the design
           * has a few, where the section heading above is the name — would otherwise point
           * at an element that does not exist and end up with no accessible name at all.
           */
          aria-labelledby={label ? `${finalName}-label` : undefined}
        >
          {options.map(
            (
              { value, label, ...rest }: ToggleButtonGroupBaseOption,
              i: number,
            ) => (
              <ToggleButton
                key={value}
                value={value}
                data-testid="ToggleButtonGroupField.ToggleButton"
                sx={theme => ({
                  borderColor: !!error ? theme.palette.error.main : undefined,
                  minWidth: '40px',
                })}
                ref={i === 0 ? (ref as Ref<HTMLButtonElement>) : null}
                size={toggleButtonSize}
                {...rest}
              >
                {label}
              </ToggleButton>
            ),
          )}
        </ToggleButtonGroup>
        <FormHelperText>
          <IncFormHelperText
            error={error}
            helperText={helperText}
            severity={severity}
          />
        </FormHelperText>
      </FormControl>
    );
  },
);

export default ToggleButtonGroupBase;
